import { lazy, Suspense, useEffect, useState } from "react";
import { Card } from "../../components/ui/Card";
import { Eyebrow } from "../../components/ui/Eyebrow";
import { Chip } from "../../components/ui/Chip";
import { formatNumber } from "../../lib/format";

const BodyScanner3D = lazy(() => import("./BodyScanner3D"));

function hasWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return !!(canvas.getContext("webgl") || canvas.getContext("experimental-webgl"));
  } catch {
    return false;
  }
}

interface Props {
  chestValue: number | null;
  chestDelta: number | null;
  waistValue: number | null;
  waistDelta: number | null;
}

export function ScannerSection({ chestValue, chestDelta, waistValue, waistDelta }: Props) {
  const [supported, setSupported] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    setSupported(hasWebGL());
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
  }, []);

  return (
    <Card>
      <Eyebrow right={<Chip>2 zonas foco</Chip>}>Escáner corporal</Eyebrow>
      <div className="relative h-[330px] overflow-hidden rounded-inner" style={{ background: "radial-gradient(70% 60% at 50% 45%, rgba(46,107,255,.10), transparent 70%)" }}>
        {supported ? (
          <Suspense fallback={<div className="absolute inset-0" />}>
            <BodyScanner3D reducedMotion={reducedMotion} />
          </Suspense>
        ) : (
          <div className="absolute inset-0 grid place-items-center font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
            Vista 3D no disponible en este dispositivo
          </div>
        )}

        <div className="absolute left-3 top-2.5 z-[3] flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-cyan">
          <span className="h-[5px] w-[5px] animate-pulse rounded-full bg-cyan shadow-[0_0_8px_#3BE0FF]" />
          Escaneando · hoy vs inicio
        </div>

        <div className="absolute right-2 z-[3] min-w-[112px] -translate-y-1/2 rounded-xl border border-accent-bright/28 bg-[rgba(8,12,24,.82)] px-[11px] py-2 backdrop-blur-md" style={{ top: "34%" }}>
          <small className="mb-0.5 block font-mono text-[8.5px] uppercase tracking-[0.14em] text-accent-bright">Pecho</small>
          <b className="font-display text-[21px] font-bold leading-none">
            {formatNumber(chestValue)} cm{" "}
            {chestDelta != null && (
              <span className="font-mono text-[10px] text-cyan">
                {chestDelta <= 0 ? "▼" : "▲"}{formatNumber(Math.abs(chestDelta))}
              </span>
            )}
          </b>
        </div>

        <div className="absolute left-2 z-[3] min-w-[112px] -translate-y-1/2 rounded-xl border border-accent-bright/28 bg-[rgba(8,12,24,.82)] px-[11px] py-2 backdrop-blur-md" style={{ top: "58%" }}>
          <small className="mb-0.5 block font-mono text-[8.5px] uppercase tracking-[0.14em] text-accent-bright">Cintura</small>
          <b className="font-display text-[21px] font-bold leading-none">
            {formatNumber(waistValue)} cm{" "}
            {waistDelta != null && (
              <span className="font-mono text-[10px] text-cyan">
                {waistDelta <= 0 ? "▼" : "▲"}{formatNumber(Math.abs(waistDelta))}
              </span>
            )}
          </b>
        </div>
      </div>

      <div className="mt-3 flex gap-4 font-mono text-[9.5px] tracking-[0.08em] text-muted">
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-0 w-[18px] border-t-2 border-[#55617C]" />Hoy</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-0 w-[18px] border-t-2 border-dashed border-accent" />Inicio</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2 w-2 rounded-full border-[1.5px] border-accent-bright" />Zona foco</span>
      </div>
    </Card>
  );
}
